// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  value: string,
  activeCategory: Object,
  options: Array<Object>,
  showAsButton: Boolean,
  disabled: Boolean,
  lastItem: Boolean,
  onPathChange: () => void
}

// Helper function to transform cxc10 display to AIXC
const transformDisplayTitle = (title) => {
  if (!title) return title;
  // Case-insensitive replacement of cxc10 to AIXC
  return title.replace(/cxc10/gi, 'AIXC');
};

// Choosing a level from the keyboard re-draws the picker and removes the focused name; the new
// last-level dropdown takes the focus instead, so keyboard users don't land back at the top.
let focusNextSelect = false;

// The clickable names are divs; this makes them buttons for the keyboard, screen readers and AI assistants.
const asButton = (onActivate, label) => ({
  role: 'button',
  tabIndex: 0,
  title: label,
  onClick: onActivate,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      focusNextSelect = true;
      onActivate();
    }
  }
});

const takeFocus = (el) => {
  if (el && focusNextSelect) {
    focusNextSelect = false;
    el.focus();
  }
};

function ChainSelectorItem({ classes, value, activeCategory, options,
                             onPathChange, showAsButton, disabled, lastItem }: Props) {
  const expandArrow = (
    disabled ? (
      <span
        className="material-icons"
      >
        keyboard_arrow_right
      </span>
    ) : (
      <span
        className={classNames('material-icons', classes.arrowRight)}
      >
        keyboard_arrow_right
      </span>
    ));

  const selector = (
    <div
      className={classNames({ [classes.arrowOnHover]: !showAsButton })}
    >
      <select
        ref={takeFocus}
        className={classNames(classes.control, classes.select)}
        aria-label="Category"
        value={value}
        onChange={e => onPathChange(
          e.target.value,
          e.target[e.target.selectedIndex].getAttribute('data-category'),
          e.target[e.target.selectedIndex].getAttribute('data-canuseredit') === 'true')}
      >
        { options.map(option =>
          <option
            key={option.path}
            value={option.path}
            data-category={option._id}
            data-canuseredit={option.canUserEdit}
          >{transformDisplayTitle(option.title)}</option>
        )}
      </select>
    </div>
  );

  const button = (
    disabled ? (
      <div
        className={classNames(classes.control, classes.button, classes.buttonDisabled)}
      >
        {transformDisplayTitle(activeCategory.title)}
      </div>
    ) : (
      <div
        className={classNames(classes.control, classes.button)}
        {...asButton(() => onPathChange(
          activeCategory.path, activeCategory._id, activeCategory.canUserEdit),
        `Choose ${transformDisplayTitle(activeCategory.title)} again, or another category at this level`)}
      >
        {transformDisplayTitle(activeCategory.title)}
      </div>
    )
  );

  const hasChildren = activeCategory.children && activeCategory.children.length > 0;
  const firstChild = hasChildren && activeCategory.children[0];
  const nextChainItem = (
    hasChildren &&
    <div
      className={classes.nextChainItem}
      {...asButton(() => onPathChange(firstChild.path, firstChild._id, firstChild.canUserEdit),
        `Select ${transformDisplayTitle(firstChild.title)}`)}
    >
      {transformDisplayTitle(activeCategory.children[0].title)}
    </div>
  );

  return (
    <div className={classes.container}>
      { (disabled || showAsButton) && button }
      { !disabled && !showAsButton && selector }
      { hasChildren && expandArrow }
      { !disabled && lastItem && nextChainItem }
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    transition: 'all, .25s',
    '& > .material-icons': {
      fontSize: '16px',
      color: 'hsl(0, 0%, 20%)',
      transition: 'all .25s',
    },
    [DARK]: { '& > .material-icons': { color: S.muted } }
  },
  arrowOnHover: {
    position: 'relative',
    '&:hover:after': {
      fontFamily: 'Material Icons',
      content: '"\\e313"',
      color: 'hsl(0, 0%, 50%)',
      fontWeight: '400',
      fontSize: '12px',
      display: 'block',
      pointerEvents: 'none',
      position: 'absolute',
      bottom: '0',
      left: '55px',
    },
    [DARK]: { '&:hover:after': { color: S.muted } }
  },
  control: {
    display: 'inline-block',
    border: 'none',
    borderRadius: '3px',
    fontSize: '13px',
    letterSpacing: '0',
    fontFamily: '"Montserrat", sans-serif',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '400',
    height: '38px',
    width: '120px',
    backgroundColor: 'transparent',
    transition: 'background-color .25s',
    cursor: 'pointer',
    position: 'relative',
    padding: '0 10px',
    '&:hover': {
      backgroundColor: 'hsl(0, 0%, 95%)',
      boxShadow: '0px 3px 8px 1px hsl(0, 0%, 80%)',
    },
    [DARK]: {
      color: S.fg,
      '&:hover': { backgroundColor: S.chromeHover, boxShadow: A.pop }
    }
  },
  buttonDisabled: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    [DARK]: { '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' } }
  },
  select: {
    outline: 'none',
    '&:focus-visible': A.focus,
    '-webkit-appearance': 'none',
    '-moz-appearance': 'none',
    '&:-ms-expand': {
      display: 'none'
    }
  },
  button: {
    lineHeight: '38px',
    '&:focus-visible': A.focus
  },
  nextChainItem: {
    '&:focus-visible': A.focus,
    display: 'inline-block',
    borderRadius: '3px',
    fontSize: '13px',
    letterSpacing: '0',
    fontFamily: '"Montserrat", sans-serif',
    // A choice to click, not a disabled field.
    backgroundColor: S.field,
    color: 'hsl(0, 0%, 20%)',
    border: '1px dashed hsl(214, 9%, 72%)',
    fontWeight: '400',
    height: '38px',
    width: '120px',
    transition: 'all .25s',
    cursor: 'pointer',
    position: 'relative',
    padding: '0 10px',
    lineHeight: '38px',
    '&:hover': {
      boxShadow: '0px 3px 8px 1px hsl(0, 0%, 80%)',
      borderStyle: 'solid'
    },
    [DARK]: {
      color: S.fg,
      borderColor: S.fieldLine,
      '&:hover': { boxShadow: A.pop }
    }
  }
};

export default injectSheet(styles)(ChainSelectorItem);
